/**
 * domino-balance.ts
 * Page orchestrator for the Domino Balance puzzle game.
 *
 * Flow: difficulty selection → start game → render board + tile pool →
 * drag-and-drop / tap-to-place → check solution → EOG overlay.
 */

import { logger } from '../lib/logger.js';
import { showError, showSuccess, showInfo } from '../lib/toast.js';
import { showResumeBanner } from '../lib/resume-banner.js';
import {
  showEndOfGameOverlay,
  buildSoloWinConfig,
} from '../components/end-of-game-overlay.js';
import type { NextGameSuggestions } from '../components/end-of-game-overlay.js';
import {
  fireGuestEvent,
  fireGameCompleted,
  hookGuestCompletionToEOG,
} from '../lib/guest-conversion-engine.js';
import { isGuestUser } from '../lib/auth-state.js';
import { initAnimationsModal } from '../components/animation-tier-modal.js';
import { getAnimationPref } from '../lib/animation-pref.js';
import type { AnimationTier } from '../lib/animation-pref.js';
import { safeInitCelebrations } from '../utils/celebration-handler.js';
import { runSequence, type SequenceHandle } from '../lib/animation-orchestrator.js';
import { fireAndForgetCelebration, delayEffect } from '../lib/animation-effects.js';
import { ensureAuthModalExists, authModal } from '../components/auth-modal.js';
import { balanceService } from '../services/balance-service.js';
import { mountOpponentBar, showCoachBar } from '../components/opponent-bar.js';

import { DominoBoardRenderer } from '../lib/domino-board.js';
import { DominoInteractionManager } from '../lib/domino-interaction.js';
import { renderBoardDominoTile } from '../lib/domino-faces.js';
import type { DominoTileData, DominoOrientation, TilePlacement } from '../lib/domino-types.js';
import { getDisplayTile } from '../lib/domino-types.js';

import * as api from '../games/domino_balance/api.js';
import type {
  DominoBalanceState,
  ConstraintStatus,
  DominoBalancePlacement,
  CheckError,
} from '../games/domino_balance/types.js';

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let gameId: string | null = null;
let gameState: DominoBalanceState | null = null;
let playEventId: string | null = null;
let boardRenderer: DominoBoardRenderer | null = null;
let interaction: DominoInteractionManager | null = null;
let isGuest = false;
let animationLevel: AnimationTier = 'playful';
let eogHandle: SequenceHandle | undefined;

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

function show(id: string): void {
  const el = document.getElementById(id);
  if (el) el.style.display = '';
}

function hide(id: string): void {
  const el = document.getElementById(id);
  if (el) el.style.display = 'none';
}

function setDisabled(id: string, disabled: boolean): void {
  const el = document.getElementById(id) as HTMLButtonElement | null;
  if (el) el.disabled = disabled;
}

// ---------------------------------------------------------------------------
// Guest status
// ---------------------------------------------------------------------------

async function loadGuestStatus(): Promise<void> {
  try {
    const resp = await fetch('/me', { credentials: 'same-origin' });
    if (resp.ok) {
      const data = await resp.json();
      isGuest = isGuestUser(data);
    } else {
      isGuest = true;
    }
  } catch {
    isGuest = true;
  }
  logger.info('[DominoBalance] Guest status:', isGuest);
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

async function loadStats(): Promise<void> {
  if (isGuest) {
    const statsCard = document.getElementById('statsCard');
    if (statsCard) {
      const cardBody = statsCard.querySelector('.card-body');
      if (cardBody) {
        cardBody.innerHTML = `
          <h6 class="text-muted mb-3">Your Stats</h6>
          <div class="text-center py-3" data-testid="guest-stats-placeholder">
            <p class="mb-1 text-muted">Your puzzle stats live here</p>
            <p class="small text-muted mb-2">Sign in to start tracking your wins!</p>
            <a href="#" class="btn btn-outline-primary btn-sm" data-testid="guest-stats-signin">
              Sign in &amp; start earning →
            </a>
          </div>`;
        const signinLink = cardBody.querySelector('[data-testid="guest-stats-signin"]');
        if (signinLink) {
          signinLink.addEventListener('click', (e) => {
            e.preventDefault();
            void fireGuestEvent('tried_locked_feature', true).then((shown) => {
              if (!shown) {
                ensureAuthModalExists();
                authModal.show();
              }
            });
          });
        }
      }
    }
    return;
  }

  try {
    const stats = await api.getStats();
    const winRate = document.getElementById('statWinRate');
    const wins = document.getElementById('statWins');
    const games = document.getElementById('statGames');

    if (winRate) winRate.textContent = stats.games_played > 0 ? `${stats.win_rate}%` : '-';
    if (wins) wins.textContent = stats.games_played > 0 ? String(stats.wins) : '-';
    if (games) games.textContent = stats.games_played > 0 ? String(stats.games_played) : '-';
  } catch {
    logger.warn('[DominoBalance] Could not load stats');
  }
}

// ---------------------------------------------------------------------------
// Board rendering
// ---------------------------------------------------------------------------

function renderBoard(state: DominoBalanceState): void {
  const container = document.getElementById('boardContainer');
  if (!container) return;
  container.innerHTML = '';
  container.dataset.animationTier = animationLevel;

  boardRenderer = new DominoBoardRenderer(container, state.grid_rows, state.grid_cols, {
    tileSize: 'responsive',
  });
  boardRenderer.renderEmptyBoard();

  // Show row targets
  for (let r = 0; r < state.row_targets.length; r++) {
    boardRenderer.showRowTarget(r, state.row_targets[r]);
  }
  // Show column targets
  for (let c = 0; c < state.col_targets.length; c++) {
    boardRenderer.showColTarget(c, state.col_targets[c]);
  }

  // Place pre-placed tiles (non-interactive, locked)
  for (const p of state.pre_placed) {
    boardRenderer.placeTile(p.row, p.col, p.tile as DominoTileData, p.orientation, undefined, true);
  }

  // Place any existing player placements (resume case)
  for (const p of state.placements) {
    boardRenderer.placeTile(p.row, p.col, p.tile as DominoTileData, p.orientation);
  }
}

function renderTilePool(tiles: DominoTileData[]): void {
  const poolEl = document.getElementById('tilePool');
  if (!poolEl) return;
  poolEl.innerHTML = '';

  // Inherit --cell-size from the board for responsive pool tiles.
  // Use requestAnimationFrame to ensure the board has been laid out
  // and --cell-size has been computed before sizing pool tiles.
  const boardContainer = document.getElementById('boardContainer');
  const cellSize = boardContainer?.style.getPropertyValue('--cell-size');
  if (cellSize) poolEl.style.setProperty('--cell-size', cellSize);

  const buildPoolTiles = (): void => {
    for (const [a, b] of tiles) {
      const tileEl = renderBoardDominoTile(a, b, 'vertical', 'responsive');
      tileEl.dataset.tileA = String(a);
      tileEl.dataset.tileB = String(b);
      tileEl.dataset.orientation = 'vertical';
      tileEl.style.touchAction = 'none';
      tileEl.style.cursor = 'pointer';
      poolEl.appendChild(tileEl);
    }
  };

  // If --cell-size is already set, render immediately; otherwise defer
  // one frame so the ResizeObserver can fire first.
  if (cellSize) {
    buildPoolTiles();
  } else {
    requestAnimationFrame(() => {
      const freshSize = boardContainer?.style.getPropertyValue('--cell-size');
      if (freshSize) poolEl.style.setProperty('--cell-size', freshSize);
      buildPoolTiles();
    });
  }
}

function setupInteraction(): void {
  const poolEl = document.getElementById('tilePool');
  if (!boardRenderer || !poolEl) return;

  // Destroy previous interaction manager if any
  if (interaction) {
    interaction.destroy();
    interaction = null;
  }

  interaction = new DominoInteractionManager(boardRenderer, poolEl, {
    enableDrag: true,
    enableTap: true,
    enableMove: true,
  });
  interaction.enableDragDrop();
  interaction.enableRotation();

  interaction.onTilePlaced(handleTilePlaced);
  interaction.onTileRemoved(handleTileRemoved);
  interaction.onTileMoved(handleTileMoved);
  interaction.onTileRotated(handleTileRotated);
}

// ---------------------------------------------------------------------------
// Constraint status updates
// ---------------------------------------------------------------------------

function updateConstraintIndicators(
  rows: ConstraintStatus[],
  cols: ConstraintStatus[],
): void {
  if (!boardRenderer) return;
  const container = boardRenderer.getCell(0, 0)?.parentElement;
  if (!container) return;

  // Update row targets
  const rowTargets = container.querySelectorAll('.board-target-row');
  for (const el of rowTargets) {
    const idx = parseInt((el as HTMLElement).dataset.row ?? '-1', 10);
    const status = rows.find(r => r.index === idx);
    el.classList.remove('board-target--satisfied', 'board-target--over');
    if (status) {
      if (status.satisfied) {
        el.classList.add('board-target--satisfied');
      } else if (status.current_sum > status.target) {
        el.classList.add('board-target--over');
      }
    }
  }

  // Update column targets
  const colTargets = container.querySelectorAll('.board-target-col');
  for (const el of colTargets) {
    const idx = parseInt((el as HTMLElement).dataset.col ?? '-1', 10);
    const status = cols.find(c => c.index === idx);
    el.classList.remove('board-target--satisfied', 'board-target--over');
    if (status) {
      if (status.satisfied) {
        el.classList.add('board-target--satisfied');
      } else if (status.current_sum > status.target) {
        el.classList.add('board-target--over');
      }
    }
  }
}

function updateControls(): void {
  if (!gameState) return;
  setDisabled('undoBtn', (gameState.placements?.length ?? 0) === 0);
}

// ---------------------------------------------------------------------------
// Tile interaction handlers
// ---------------------------------------------------------------------------

async function handleTilePlaced(placement: TilePlacement): Promise<void> {
  if (!gameId || !gameState) return;

  try {
    const displayTile = getDisplayTile(placement.tile, placement.rotation ?? 0);
    const res = await api.makeMove({
      game_id: gameId,
      row: placement.row,
      col: placement.col,
      tile: displayTile,
      orientation: placement.orientation,
    });

    gameState.placements = res.state.placements;
    gameState.moves_made = res.state.moves_made;
    gameState.constraint_status = res.state.constraint_status;

    // Update remaining tile pool count from placements delta
    gameState.tile_pool = gameState.tile_pool.filter(t => {
      const pA = t[0];
      const pB = t[1];
      const tileMatch = (pA === placement.tile[0] && pB === placement.tile[1]) ||
                        (pA === placement.tile[1] && pB === placement.tile[0]);
      return !tileMatch;
    });

    updateConstraintIndicators(
      res.state.constraint_status.rows,
      res.state.constraint_status.cols,
    );
    updateControls();

    // Handle first-correct tile bonus
    if (res.first_correct?.awarded) {
      const bonus = res.first_correct.bonus_fp;
      safeInitCelebrations();
      fireAndForgetCelebration('bonus');
      balanceService.refresh().catch(err => logger.error('FP refresh failed:', err));
      showSuccess(`Nice start! +${bonus} FP ✨`);
    }

    // Handle auto-check (all tiles placed)
    if (res.auto_check) {
      if (res.auto_check.correct) {
        // Disable further interaction
        interaction?.disableDragDrop();
        interaction?.disableRotation();
        window.__versionGuard?.setSafeToReload(true);

        // Refresh FP balance
        if (res.auto_check.fp_earned > 0) {
          balanceService.refresh().catch(err => logger.error('FP refresh failed:', err));
        }

        handleWin(res.auto_check.fp_earned, res.auto_check.next_game_suggestions);
      } else {
        showWrongSolutionModal(res.auto_check.errors);
      }
    }
  } catch (err) {
    // Revert the visual placement on error
    boardRenderer?.removeTile(placement.row, placement.col);
    renderTilePool(gameState.tile_pool);
    const msg = err instanceof Error ? err.message : 'That tile didn\'t fit — try another spot!';
    showError(msg);
    logger.error('[DominoBalance] Move error:', err);
  }
}

async function handleTileRemoved(placement: TilePlacement): Promise<void> {
  if (!gameId || !gameState) return;

  // A tile was removed from the board back to the pool via the interaction manager.
  // We need to send an undo to the server. But the interaction manager handles
  // arbitrary removal, not necessarily the last move. For simplicity, we re-sync
  // by calling undo on the server and re-rendering from server state.
  try {
    const res = await api.undoMove(gameId);
    gameState.placements = res.state.placements;
    gameState.moves_made = res.state.moves_made;
    gameState.constraint_status = res.state.constraint_status;

    if (res.removed) {
      gameState.tile_pool.push(res.removed.tile as DominoTileData);
    }

    updateConstraintIndicators(
      res.state.constraint_status.rows,
      res.state.constraint_status.cols,
    );
    updateControls();
  } catch (err) {
    logger.error('[DominoBalance] Remove/undo error:', err);
    showError('Undo hiccup — refresh and you\'re back!');
  }
}

async function handleTileMoved(from: TilePlacement, to: TilePlacement): Promise<void> {
  // A tile was moved on the board. Use the atomic reposition endpoint.
  if (!gameId || !gameState) return;

  try {
    const displayTile = getDisplayTile(to.tile, to.rotation ?? 0);
    const res = await api.repositionTile({
      game_id: gameId,
      from_row: from.row,
      from_col: from.col,
      to_row: to.row,
      to_col: to.col,
      tile: displayTile,
      orientation: to.orientation,
    });

    gameState.placements = res.state.placements;
    gameState.moves_made = res.state.moves_made;
    gameState.constraint_status = res.state.constraint_status;

    updateConstraintIndicators(
      res.state.constraint_status.rows,
      res.state.constraint_status.cols,
    );
    updateControls();

    // Handle auto-check (all tiles placed after reposition)
    if (res.auto_check) {
      if (res.auto_check.correct) {
        interaction?.disableDragDrop();
        interaction?.disableRotation();
        window.__versionGuard?.setSafeToReload(true);

        if (res.auto_check.fp_earned > 0) {
          balanceService.refresh().catch(err => logger.error('FP refresh failed:', err));
        }

        handleWin(res.auto_check.fp_earned, res.auto_check.next_game_suggestions);
      } else {
        showWrongSolutionModal(res.auto_check.errors);
      }
    }
  } catch (err) {
    logger.error('[DominoBalance] Move error:', err);
    showError('Oops, that move didn\'t stick — try again!');
  }
}

// ---------------------------------------------------------------------------
// Button handlers
// ---------------------------------------------------------------------------

async function handleTileRotated(placement: TilePlacement): Promise<void> {
  // A placed tile was rotated. Sync the new tile order to the server via reposition.
  if (!gameId || !gameState) return;

  try {
    const displayTile = getDisplayTile(placement.tile, placement.rotation ?? 0);
    const res = await api.repositionTile({
      game_id: gameId,
      from_row: placement.row,
      from_col: placement.col,
      to_row: placement.row,
      to_col: placement.col,
      tile: displayTile,
      orientation: placement.orientation,
    });

    gameState.placements = res.state.placements;
    gameState.moves_made = res.state.moves_made;
    gameState.constraint_status = res.state.constraint_status;

    updateConstraintIndicators(
      res.state.constraint_status.rows,
      res.state.constraint_status.cols,
    );
    updateControls();

    // Handle auto-check (all tiles placed after rotation)
    if (res.auto_check) {
      if (res.auto_check.correct) {
        interaction?.disableDragDrop();
        interaction?.disableRotation();
        window.__versionGuard?.setSafeToReload(true);

        if (res.auto_check.fp_earned > 0) {
          balanceService.refresh().catch(err => logger.error('FP refresh failed:', err));
        }

        handleWin(res.auto_check.fp_earned, res.auto_check.next_game_suggestions);
      } else {
        showWrongSolutionModal(res.auto_check.errors);
      }
    }
  } catch (err) {
    logger.error('[DominoBalance] Rotation sync error:', err);
  }
}

async function handleUndo(): Promise<void> {
  if (!gameId || !gameState) return;

  try {
    const res = await api.undoMove(gameId);

    gameState.placements = res.state.placements;
    gameState.moves_made = res.state.moves_made;
    gameState.constraint_status = res.state.constraint_status;

    if (res.removed) {
      gameState.tile_pool.push(res.removed.tile as DominoTileData);
    }

    // Re-render the board and pool from current state
    renderBoard(gameState);
    renderTilePool(gameState.tile_pool);
    setupInteraction();

    if (gameState.constraint_status) {
      updateConstraintIndicators(
        gameState.constraint_status.rows,
        gameState.constraint_status.cols,
      );
    }
    updateControls();
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Undo hiccup — refresh and you\'re back!';
    showError(msg);
    logger.error('[DominoBalance] Undo error:', err);
  }
}

async function handleHint(): Promise<void> {
  if (!gameId || !gameState) return;

  try {
    const res = await api.getHint(gameId);

    if (!res.success) {
      showError(res.error ?? 'Hint not available right now');
      return;
    }

    // Highlight the hinted cell with a pulse
    if (boardRenderer) {
      const cell = boardRenderer.getCell(res.hint.row, res.hint.col);
      if (cell) {
        // Show ghost pip value (keep previous ghosts visible)
        let ghost = cell.querySelector('.domino-hint-ghost') as HTMLElement | null;
        if (!ghost) {
          ghost = document.createElement('span');
          ghost.className = 'domino-hint-ghost';
          cell.appendChild(ghost);
        }
        ghost.textContent = String(res.hint.value);

        // Force-retrigger pulse animation (remove → reflow → add)
        cell.classList.remove('domino-cell--hinted');
        void cell.offsetWidth;
        cell.classList.add('domino-cell--hinted');
        cell.addEventListener('animationend', () => {
          cell.classList.remove('domino-cell--hinted');
        }, { once: true });
      }
    }

    // Refresh balance
    balanceService.refresh().catch(err => logger.error('FP refresh failed:', err));

    showSuccess(`Try placing a ${res.hint.value} here!`);
  } catch (err) {
    logger.error('[DominoBalance] Hint error:', err);
    showError('Hint unavailable right now — try again!');
  }
}

// ---------------------------------------------------------------------------
// Wrong-solution modal (board full but incorrect)
// ---------------------------------------------------------------------------

const WRONG_SOLUTION_MODAL_ID = 'wrongSolutionModal';

function dismissWrongSolutionModal(): void {
  const existing = document.getElementById(WRONG_SOLUTION_MODAL_ID);
  if (existing) {
    const inst = bootstrap.Modal.getInstance(existing);
    if (inst) inst.dispose();
    existing.remove();
  }
}

function showWrongSolutionModal(errors: CheckError[]): void {
  dismissWrongSolutionModal();

  const errorSummary = errors
    .map(e => {
      const label = e.type === 'row' ? `Row ${e.index + 1}` : `Col ${e.index + 1}`;
      return `${label}: ${e.current_sum} / ${e.target}`;
    })
    .join(', ');

  const modalEl = document.createElement('div');
  modalEl.id = WRONG_SOLUTION_MODAL_ID;
  modalEl.className = 'modal fade';
  modalEl.setAttribute('tabindex', '-1');
  modalEl.setAttribute('aria-labelledby', 'wrongSolutionLabel');
  modalEl.setAttribute('aria-hidden', 'true');
  modalEl.innerHTML = `
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content">
        <div class="modal-header border-0 pb-0">
          <h6 class="modal-title" id="wrongSolutionLabel">Not quite right!</h6>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body text-center pt-2">
          <p class="mb-2">Some sums don\u2019t add up yet.</p>
          <p class="mb-3 small text-muted">${errorSummary}</p>
          <div class="d-grid gap-2">
            <button type="button" class="btn btn-primary" data-action="continue">Continue</button>
            <button type="button" class="btn btn-outline-secondary" data-action="reveal">Reveal Solution</button>
            <button type="button" class="btn btn-outline-secondary" data-action="restart">Restart</button>
          </div>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modalEl);

  const modal = new bootstrap.Modal(modalEl, { backdrop: true, keyboard: true });
  modal.show();

  modalEl.addEventListener('hidden.bs.modal', () => {
    modalEl.remove();
  }, { once: true });

  // Wire button actions
  modalEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
    if (!btn) return;
    const action = btn.dataset.action;
    modal.hide();

    if (action === 'continue') {
      // Just dismiss — user can keep adjusting tiles
      return;
    }
    if (action === 'reveal') {
      void handleRevealSolution();
      return;
    }
    if (action === 'restart') {
      const difficulty = gameState?.difficulty;
      resetToSelection();
      if (difficulty) void startGame(difficulty);
    }
  });
}

async function handleRevealSolution(): Promise<void> {
  if (!gameId || !gameState) return;

  try {
    const res = await api.revealSolution(gameId);

    // Disable interaction — game is over
    interaction?.disableDragDrop();
    interaction?.disableRotation();
    window.__versionGuard?.setSafeToReload(true);

    // Re-render the board with the correct solution placements
    if (boardRenderer && res.solution_placements) {
      // Clear all player-placed tiles and re-render with solution
      gameState.placements = res.solution_placements.filter(sp => {
        // Only show tiles that aren't pre-placed
        const isPrePlaced = (gameState!.pre_placed || []).some(
          pp => pp.row === sp.row && pp.col === sp.col,
        );
        return !isPrePlaced;
      });

      renderBoard(gameState);
      renderTilePool([]);  // Empty pool — all tiles placed

      if (gameState.constraint_status) {
        updateConstraintIndicators(
          gameState.constraint_status.rows,
          gameState.constraint_status.cols,
        );
      }
    }

    showInfo('Here\u2019s the solution \u2014 better luck next time! \ud83d\udca1', 5000);
    loadStats();
  } catch (err) {
    logger.error('[DominoBalance] Reveal error:', err);
    showError('Could not reveal the solution \u2014 try again!');
  }
}

// ---------------------------------------------------------------------------
// End-of-game
// ---------------------------------------------------------------------------

function handleWin(fpEarned: number, nextGameSuggestions?: NextGameSuggestions): void {
  const onPlayAgain = () => {
    void fireGameCompleted(isGuest);
    resetToSelection();
  };

  const config = buildSoloWinConfig({
    game: 'domino-balance',
    totalScore: 0,
    fpEarned,
    isGuest,
    onPlayAgain,
    playAgainUrl: '/games/domino-balance',
    playEventId: playEventId ?? undefined,
    nextGameSuggestions,
  });

  config.message = '🧩 Nailed it!';
  config.subtitle = 'Your brain is on fire! 🔥';

  // Cancel any pending EOG sequence
  eogHandle?.cancel();

  // Orchestrated: fire-and-forget celebration → 800ms delay → overlay
  eogHandle = runSequence('domino-balance-eog', [
    fireAndForgetCelebration('win'),
    delayEffect(800),
  ], () => {
    showEndOfGameOverlay(config);
    hookGuestCompletionToEOG(isGuest);
    loadStats();
  });
}

// ---------------------------------------------------------------------------
// Game lifecycle
// ---------------------------------------------------------------------------

async function startGame(difficulty: string): Promise<void> {
  try {
    const res = await api.startGame({ difficulty });

    gameId = res.game_id;
    gameState = res.state;
    playEventId = res.play_event_id ?? null;

    window.__versionGuard?.setSafeToReload(false);

    hide('setupSection');
    show('gameSection');

    renderBoard(gameState);
    renderTilePool(gameState.tile_pool);
    setupInteraction();
    updateControls();

    // Mount opponent bar
    void mountOpponentBarIfAvailable();

    logger.info('[DominoBalance] Game started:', gameId, difficulty);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Couldn\'t get your puzzle ready — try again!';
    showError(msg);
    logger.error('[DominoBalance] Start error:', err);
  }
}

function resumeGame(id: string, state: DominoBalanceState): void {
  gameId = id;
  gameState = state;

  window.__versionGuard?.setSafeToReload(false);

  hide('setupSection');
  show('gameSection');

  renderBoard(state);
  renderTilePool(state.tile_pool);
  setupInteraction();
  updateControls();

  // Show constraint status if available
  if (state.constraint_status) {
    updateConstraintIndicators(
      state.constraint_status.rows,
      state.constraint_status.cols,
    );
  }

  // Mount opponent bar
  void mountOpponentBarIfAvailable();

  // Show resume banner
  const container = document.getElementById('gameSection');
  if (container) {
    showResumeBanner({
      container,
      onAbandon: () => {
        const idToAbandon = gameId;
        resetToSelection();
        if (idToAbandon) {
          api.abandonGame(idToAbandon).catch(() => {
            // Best-effort — /start will force-abandon on next new game anyway
          });
        }
      },
    });
  }

  logger.info('[DominoBalance] Game resumed:', id);
}

async function checkActiveGame(): Promise<boolean> {
  try {
    const res = await api.getActiveGame();
    if (res.game_id && res.state) {
      resumeGame(res.game_id, res.state);
      return true;
    }
  } catch {
    logger.warn('[DominoBalance] Could not check for active game');
  }
  return false;
}

function resetToSelection(): void {
  window.__versionGuard?.setSafeToReload(true);

  // Cancel any pending EOG orchestration
  eogHandle?.cancel();
  eogHandle = undefined;

  // Destroy interaction manager
  if (interaction) {
    interaction.destroy();
    interaction = null;
  }

  gameId = null;
  gameState = null;
  playEventId = null;
  boardRenderer = null;

  hide('gameSection');
  show('setupSection');

  loadStats();
}

// ---------------------------------------------------------------------------
// Opponent bar & hint button
// ---------------------------------------------------------------------------

async function mountOpponentBarIfAvailable(): Promise<void> {
  const container = document.getElementById('opponentBarContainer');
  if (!container) return;

  const coachHintsBtn = document.getElementById('coachHintsBtn');

  try {
    await mountOpponentBar({
      container,
      gameType: 'domino_balance',
      variant: 'coach',
      onHintRequest: () => void handleHint(),
      hintLabel: 'Ask for Hint (3 FP)',
      onHide: () => {
        if (coachHintsBtn) coachHintsBtn.style.removeProperty('display');
      },
    });

    if (coachHintsBtn) {
      coachHintsBtn.addEventListener('click', () => {
        coachHintsBtn.style.display = 'none';
        showCoachBar();
      });
    }
  } catch (err) {
    logger.warn('[DominoBalance] Could not mount opponent bar:', err);
  }
}

// ---------------------------------------------------------------------------
// 

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

async function init(): Promise<void> {
  logger.info('[DominoBalance] Initializing');

  // Load guest status then stats
  await loadGuestStatus();
  loadStats();

  // Animation preferences
  animationLevel = getAnimationPref();
  initAnimationsModal({
    gamePrefix: 'dominoBalance',
    cssClassPrefix: 'domino-balance-anim',
    pageElementId: 'gameSection',
    onTierChange: (tier) => {
      animationLevel = tier;
      const container = document.getElementById('boardContainer');
      if (container) container.dataset.animationTier = tier;
    },
  });
  safeInitCelebrations();

  // Wire difficulty card clicks via event delegation
  const setupSection = document.getElementById('setupSection');
  if (setupSection) {
    setupSection.addEventListener('click', (e) => {
      const card = (e.target as HTMLElement).closest('[data-difficulty]') as HTMLElement | null;
      if (card) {
        const difficulty = card.dataset.difficulty;
        if (difficulty) {
          void startGame(difficulty);
        }
      }
    });
  }

  // Wire control buttons
  const undoBtn = document.getElementById('undoBtn');
  if (undoBtn) {
    undoBtn.addEventListener('click', () => void handleUndo());
  }

  // Check for active game (resume)
  const hasActiveGame = await checkActiveGame();
  if (hasActiveGame) {
    logger.info('[DominoBalance] Resumed active game');
  }

  // Init tooltip on info button
  const infoBtn = document.getElementById('infoBtn');
  if (infoBtn && typeof bootstrap !== 'undefined') {
    new bootstrap.Tooltip(infoBtn);
  }
}

document.addEventListener('DOMContentLoaded', init);
